import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Search, Headphones } from 'lucide-react';
import EpisodeCard from '@/components/EpisodeCard';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArchivePage() {
  const [episodes, setEpisodes] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEpisodes = async () => {
      try {
        const { data } = await axios.get(`${API}/episodes`);
        setEpisodes(data || []);
      } catch (err) {
        console.error('Failed to fetch episodes:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchEpisodes();
  }, []);

  const filtered = episodes.filter(ep =>
    ep.title.toLowerCase().includes(search.toLowerCase()) ||
    (ep.description && ep.description.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <main data-testid="archive-page" className="pt-16 md:pt-20 min-h-screen">
      {/* Header */}
      <section className="border-b border-white/5 py-16 md:py-24 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="font-body text-xs tracking-[0.2em] uppercase font-bold text-[#FF2A2A] mb-3">
              ALL EPISODES
            </p>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl tracking-tighter uppercase leading-none mb-6">
              EPISODE ARCHIVE
            </h1>
            <p className="font-body text-base text-[#A1A1AA] max-w-lg">
              Browse through all our episodes. From day one to the latest drop.
            </p>
          </motion.div>

          {/* Search */}
          <div className="mt-8 max-w-md relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717A]" />
            <Input
              data-testid="episode-search-input"
              placeholder="Search episodes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-11 bg-[#141414] border-white/10 text-white placeholder:text-[#71717A] h-12 rounded-none focus:border-[#FF2A2A] focus:ring-1 focus:ring-[#FF2A2A]"
            />
          </div>
        </div>
      </section>

      {/* Episodes Grid */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-12 md:py-16">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-[#141414] border border-white/5 animate-pulse">
                <div className="aspect-video bg-[#1C1C1C]" />
                <div className="p-5 space-y-3">
                  <div className="h-5 bg-[#1C1C1C] w-3/4" />
                  <div className="h-4 bg-[#1C1C1C] w-1/2" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20" data-testid="no-episodes-found">
            <Headphones size={48} className="mx-auto text-[#FF2A2A]/30 mb-4" />
            <h3 className="font-heading text-2xl uppercase mb-2">
              {search ? 'NO RESULTS' : 'NO EPISODES YET'}
            </h3>
            <p className="font-body text-sm text-[#71717A]">
              {search ? 'Try a different search term.' : 'Episodes coming soon. Stay tuned!'}
            </p>
          </div>
        ) : (
          <>
            <p className="font-body text-sm text-[#71717A] mb-6" data-testid="episode-count">
              {filtered.length} episode{filtered.length !== 1 ? 's' : ''}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((ep, idx) => (
                <EpisodeCard key={ep.id} episode={ep} index={idx} />
              ))}
            </div>
          </>
        )}
      </section>
    </main>
  );
}
